import {
  defineDoctype,
  normalizeLifecycleContract,
  type DoctypeCatalogMetadata,
  type DoctypeChildCascadePolicy,
  type DoctypeDefinition,
  type DocumentLifecycleContract,
} from "@kubdee/sdk";
import { fleetVehicles } from "./schemas/vehicles";
import { maintenanceRecords } from "./schemas/maintenance-records";
import { vehicleTransfers } from "./schemas/vehicle-transfers";
import { fuelTypes } from "./schemas/fuel-types";
import { insuranceRecords } from "./schemas/insurance-records";
import { maintenanceSchedules } from "./schemas/maintenance-schedules";
import { maintenanceTypes } from "./schemas/maintenance-types";
import { registrationRecords } from "./schemas/registration-records";
import { taxRecords } from "./schemas/tax-records";
import { transmissions } from "./schemas/transmissions";
import { vehicleBodyTypes } from "./schemas/vehicle-body-types";
import { vehicleBrands } from "./schemas/vehicle-brands";
import { vehicleColors } from "./schemas/vehicle-colors";
import { fleetVehicleDocuments } from "./schemas/vehicle-documents";
import { vehicleGenerations } from "./schemas/vehicle-generations";
import { vehicleImageTypes } from "./schemas/vehicle-image-types";
import { fleetVehicleImages } from "./schemas/vehicle-images";
import { vehicleModels } from "./schemas/vehicle-models";
import { vehicleSubModels } from "./schemas/vehicle-sub-models";
import { vehicleYards } from "./schemas/vehicle-yards";

// ============================================================================
// Fleet module — registered doctypes.
//
// Plugged into the boot loop via `modules/fleet/index.ts → registeredDoctypes`;
// `apps/tenant-worker/src/middleware/auto-migrate.ts` upserts these into
// `core_doctype_registry` (platform defaults) on the first request per tenant.
//
// #423 — `maintenance` + `transfer` numbering moved off the legacy
// per-company `seed.ts` arrays onto registry `defaultSeries`. The platform
// boot loop materialises a `(module=fleet, doctype=X, company_id=NULL)`
// fallback row in `core_numbering_series`, which `findSeries()` resolves
// when no per-company override exists. Existing per-company counters keep
// running because the per-company row still wins over the NULL fallback.
//
// `vehicle` STAYS in `seed.ts` for now — it's the only fleet doctype with
// `autoGenerate = 0` (admin enters the vehicle code manually) and the
// `defaultSeries` schema does not yet expose `autoGenerate` (see
// `seedDefaultSeries` in `modules/core/services/doctype-register.ts` which
// hard-codes `autoGenerate: 1`). The UI hook
// `useIsAutoGenerate(MODULES.FLEET, "vehicle")` in
// `modules/fleet/ui/components/vehicle-form.tsx` reads that flag to
// show/hide the manual input field; flipping it silently to 1 would hide
// the field and break manual entry. Tracked as a follow-up — when the
// schema gains `autoGenerate`, vehicle can move here too.
// ============================================================================

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

export const maintenanceLifecycle: DocumentLifecycleContract =
  normalizeLifecycleContract({
    statusField: "status",
    docStatusField: null,
    draftStates: ["scheduled"],
    submittedStates: ["in_progress", "completed"],
    cancelledStates: ["cancelled"],
    immutableStates: ["completed", "cancelled"],
    editAllowedStates: ["scheduled", "in_progress"],
    actions: {
      submit: {
        label: "เริ่มซ่อม",
        from: ["scheduled"],
        to: "in_progress",
        docStatus: null,
        reversalPolicy: "none",
        activityAction: "started",
      },
      approve: {
        label: "เสร็จแล้ว",
        from: ["in_progress"],
        to: "completed",
        docStatus: null,
        reversalPolicy: "none",
        activityAction: "completed",
      },
      cancel: {
        label: "ยกเลิก",
        from: ["scheduled", "in_progress"],
        to: "cancelled",
        docStatus: null,
        reversalPolicy: "status_only",
        activityAction: "cancelled",
      },
    },
  });

export const transferLifecycle: DocumentLifecycleContract =
  normalizeLifecycleContract({
    statusField: "status",
    docStatusField: null,
    draftStates: ["draft"],
    submittedStates: ["in_transit", "completed"],
    cancelledStates: ["cancelled"],
    immutableStates: ["completed", "cancelled"],
    editAllowedStates: ["draft", "in_transit"],
    actions: {
      submit: {
        label: "ดำเนินการเสร็จ",
        from: ["draft", "in_transit"],
        to: "completed",
        docStatus: null,
        reversalPolicy: "none",
        activityAction: "completed",
      },
      cancel: {
        label: "ยกเลิก",
        from: ["draft", "in_transit"],
        to: "cancelled",
        docStatus: null,
        reversalPolicy: "status_only",
        activityAction: "cancelled",
      },
    },
  });

const CHILD_TABLE_CATALOG = (
  parentDoctype: string,
  parentField: string,
  parentKeyField: string,
  sortField?: string,
  cascadePolicy: DoctypeChildCascadePolicy = "restrict",
): DoctypeCatalogMetadata => ({
  childTable: {
    parentDoctype,
    parentField,
    parentKeyField,
    sortField: sortField ?? null,
    cascadePolicy,
  },
  policy: {
    isExportable: true,
    isReportable: true,
  },
});

/**
 * Maintenance records (`ซ่อมบำรุง`). Declared so the Doctype Admin UI can
 * expose per-company customisation (naming series, custom fields, workflow).
 *
 * Workflow feature is on by default — the workflow states match the route
 * handler at `modules/fleet/routes/maintenance.ts` which already populates
 * `fleet_maintenance_records.workflow_id` from the default workflow via
 * `useCrossModule(...).getDefaultWorkflow(...)` (#415).
 *
 * #423 — `defaultSeries.prefix` is `MT-{YYYY}-#####`, matching:
 *   - the canonical legacy seed at `modules/fleet/seed.ts` (pre-#423)
 *   - the live `generateNextNumber({ doctype: "maintenance" })` calls at
 *     `modules/fleet/routes/maintenance.ts:165`
 *   - the prod backfill at `scripts/backfill-fleet-numbering.sql:74`
 *
 * An earlier draft used `MNT-{YYYY}-{#####}` here while seed.ts used
 * `MT-` — that drift never reached running counters because the legacy
 * per-company seed always shadowed the registry default. Aligning on
 * `MT-` keeps fresh tenants on the same prefix as everyone else.
 */
export const maintenanceDoctype = defineDoctype({
  id: "maintenance",
  moduleId: "fleet",
  table: maintenanceRecords,
  label: "ซ่อมบำรุง",
  icon: "Wrench",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "maintenance_no",
  displayField: "maintenance_no",
  imageField: null,
  searchFields: ["maintenance_no", "status"],
  columnLabels: {
    maintenance_no: "เลขที่ใบซ่อม",
    vehicle_id: "ยานพาหนะ",
    maintenance_type_id: "ประเภทงานซ่อม",
    service_date: "วันที่เข้าซ่อม",
    completion_date: "วันที่ซ่อมเสร็จ",
    mileage_at_service: "เลขไมล์ ณ วันซ่อม",
    description: "รายละเอียด",
    labor_cost: "ค่าแรง",
    parts_cost: "ค่าอะไหล่",
    total_cost: "ยอดรวม",
    garage_name: "อู่ซ่อม",
    garage_contact: "เบอร์ติดต่ออู่",
    invoice_ref: "เลขที่ใบแจ้งหนี้",
    next_service_km: "นัดซ่อมครั้งถัดไป (กม.)",
    next_service_date: "นัดซ่อมครั้งถัดไป (วันที่)",
    status: "สถานะ",
    notes: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "MT-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  workflowTemplate: {
    name: "ขั้นตอนการซ่อมบำรุง",
    description: "นัดหมาย → กำลังซ่อม → เสร็จแล้ว",
    isDefault: true,
    states: [
      // Layout positions matter — without them the React Flow editor stacks
      // every state at (0,0) and the user only sees the topmost (#427 follow-up).
      // Horizontal happy-path scheduled → in_progress → completed; cancel branch
      // sits below in_progress.
      { key: "scheduled", label: "นัดหมาย", stateType: "start", color: "#F97316", sortOrder: 0, docStatus: 0, positionX: 100, positionY: 100 },
      { key: "in_progress", label: "กำลังซ่อม", stateType: "intermediate", color: "#3B82F6", sortOrder: 1, docStatus: 0, positionX: 320, positionY: 100 },
      { key: "completed", label: "เสร็จแล้ว", stateType: "success", color: "#22C55E", sortOrder: 2, docStatus: 1, positionX: 540, positionY: 100 },
      { key: "cancelled", label: "ยกเลิก", stateType: "fail", color: "#EF4444", sortOrder: 3, docStatus: 2, positionX: 320, positionY: 260 },
    ],
    transitions: [
      { fromStateKey: "scheduled", toStateKey: "in_progress", label: "เริ่มซ่อม", formType: "none", actionHook: "fleet.maintenance.startService" },
      { fromStateKey: "in_progress", toStateKey: "completed", label: "เสร็จแล้ว", formType: "none", actionHook: "fleet.maintenance.completeService" },
      { fromStateKey: "scheduled", toStateKey: "cancelled", label: "ยกเลิก", formType: "none", actionHook: "fleet.maintenance.cancelService" },
      { fromStateKey: "in_progress", toStateKey: "cancelled", label: "ยกเลิก", formType: "none", actionHook: "fleet.maintenance.cancelService" },
    ],
  },
  lifecycle: maintenanceLifecycle,
});

/**
 * Vehicle transfer records (`โอนย้ายรถ`). The `(MODULES.FLEET, "transfer")`
 * pair is the numbering key used by the live route at
 * `modules/fleet/routes/vehicle-transfers.ts:175`.
 *
 * Shares the workflow/print/permissions feature surface with maintenance —
 * the Doctype Admin UI uses it the same way.
 */
export const transferDoctype = defineDoctype({
  id: "transfer",
  moduleId: "fleet",
  table: vehicleTransfers,
  label: "โอนย้ายรถ",
  icon: "ArrowRightLeft",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "transfer_no",
  displayField: "transfer_no",
  imageField: null,
  searchFields: ["transfer_no", "status"],
  columnLabels: {
    transfer_no: "เลขที่ใบโอน",
    vehicle_id: "ยานพาหนะ",
    from_yard_id: "ลานต้นทาง",
    to_yard_id: "ลานปลายทาง",
    transfer_date: "วันที่โอน",
    reason: "เหตุผล",
    mileage_at_transfer: "เลขไมล์ ณ วันโอน",
    status: "สถานะ",
    notes: "หมายเหตุ",
    completed_at: "วันที่ดำเนินการเสร็จ",
  },
  defaultSeries: {
    prefix: "VT-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: transferLifecycle,
});

/**
 * Vehicle (`ยานพาหนะ`) — master-data doctype, manually-numbered today.
 *
 * Stored in `fleet_vehicles.vehicle_code`. Unlike maintenance / transfer,
 * the vehicle code is admin-supplied (`autoGenerate = 0` on the legacy
 * per-company series in `seed.ts`). The `defineDoctype.defaultSeries`
 * schema does not yet expose `autoGenerate` (`seedDefaultSeries` in
 * `modules/core/services/doctype-register.ts` hard-codes it to 1, and the
 * UI hook `useIsAutoGenerate(MODULES.FLEET, "vehicle")` reads that flag to
 * show/hide the manual code input). So vehicle is registered here WITHOUT
 * `defaultSeries` — the doctype shows up in the Admin UI and the legacy
 * per-company seed continues to drive the actual numbering. Once the
 * registry gains `autoGenerate`, drop the seed loop and add the
 * `defaultSeries` here.
 */
export const vehicleDoctype = defineDoctype({
  id: "vehicle",
  moduleId: "fleet",
  table: fleetVehicles,
  label: "ยานพาหนะ",
  icon: "Car",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: "vehicle_code",
  displayField: "vehicle_name",
  imageField: "image_url",
  searchFields: ["vehicle_code", "vehicle_name", "license_plate"],
  columnLabels: {
    vehicle_code: "รหัสยานพาหนะ",
    vehicle_name: "ชื่อยานพาหนะ",
    license_plate: "ทะเบียน",
    license_province: "จังหวัดทะเบียน",
    vin: "เลข VIN",
    engine_number: "เลขเครื่องยนต์",
    branch_id: "สาขา",
    yard_id: "ลานจอด",
    brand_id: "ยี่ห้อ",
    model_id: "รุ่น",
    body_type_id: "ประเภทตัวถัง",
    color_id: "สี",
    color_name: "สี (ระบุเอง)",
    manufacturing_year: "ปีผลิต",
    registration_date: "วันที่จดทะเบียน",
    transmission: "ระบบเกียร์",
    fuel_type: "ประเภทเชื้อเพลิง",
    seats: "จำนวนที่นั่ง",
    doors: "จำนวนประตู",
    mileage: "เลขไมล์",
    purchase_date: "วันที่ซื้อ",
    purchase_price: "ราคาซื้อ",
    current_value: "มูลค่าปัจจุบัน",
    ownership_type: "ประเภทการถือครอง",
    cost_center_id: "ศูนย์ต้นทุน",
    status: "สถานะ",
    notes: "หมายเหตุ",
  },
  // No `defaultSeries` — see docstring for the `autoGenerate` follow-up.
});

export const fleetBrandDoctype = defineDoctype({
  id: "fleet_brand",
  moduleId: "fleet",
  table: vehicleBrands,
  label: "ยี่ห้อรถ",
  icon: "Badge",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: "logo_url",
  searchFields: ["name", "name_th"],
});

export const fleetModelDoctype = defineDoctype({
  id: "fleet_model",
  moduleId: "fleet",
  table: vehicleModels,
  label: "รุ่นรถ",
  icon: "CarFront",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "name_th"],
});

export const fleetSubModelDoctype = defineDoctype({
  id: "fleet_sub_model",
  moduleId: "fleet",
  table: vehicleSubModels,
  label: "รุ่นย่อยรถ",
  icon: "CarFront",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "engine_size"],
});

export const fleetGenerationDoctype = defineDoctype({
  id: "fleet_generation",
  moduleId: "fleet",
  table: vehicleGenerations,
  label: "โฉมรถ",
  icon: "CalendarRange",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name"],
});

export const fleetBodyTypeDoctype = defineDoctype({
  id: "fleet_body_type",
  moduleId: "fleet",
  table: vehicleBodyTypes,
  label: "ประเภทตัวถัง",
  icon: "Shapes",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "name_th"],
});

export const fleetColorDoctype = defineDoctype({
  id: "fleet_color",
  moduleId: "fleet",
  table: vehicleColors,
  label: "สีรถ",
  icon: "Palette",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "name_th", "hex_code"],
});

export const fleetFuelTypeDoctype = defineDoctype({
  id: "fleet_fuel_type",
  moduleId: "fleet",
  table: fuelTypes,
  label: "ประเภทเชื้อเพลิง",
  icon: "Fuel",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "name_th"],
});

export const fleetTransmissionDoctype = defineDoctype({
  id: "fleet_transmission",
  moduleId: "fleet",
  table: transmissions,
  label: "ประเภทเกียร์",
  icon: "Settings2",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "name_th"],
});

export const fleetYardDoctype = defineDoctype({
  id: "fleet_yard",
  moduleId: "fleet",
  table: vehicleYards,
  label: "ลานจอดรถ",
  icon: "MapPinned",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["code", "name", "name_th", "address"],
});

export const fleetVehicleImageTypeDoctype = defineDoctype({
  id: "fleet_vehicle_image_type",
  moduleId: "fleet",
  table: vehicleImageTypes,
  label: "ประเภทรูปรถ",
  icon: "Images",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "name_en", "media_type"],
});

export const maintenanceTypeDoctype = defineDoctype({
  id: "maintenance_type",
  moduleId: "fleet",
  table: maintenanceTypes,
  label: "ประเภทการซ่อม",
  icon: "Wrench",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "category"],
});

export const maintenanceScheduleDoctype = defineDoctype({
  id: "maintenance_schedule",
  moduleId: "fleet",
  table: maintenanceSchedules,
  label: "กำหนดการซ่อมบำรุง",
  icon: "CalendarClock",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "vehicle_id",
  imageField: null,
  searchFields: ["vehicle_id", "maintenance_type_id", "next_due_date"],
});

export const registrationRecordDoctype = defineDoctype({
  id: "registration_record",
  moduleId: "fleet",
  table: registrationRecords,
  label: "ประวัติต่อทะเบียน",
  icon: "FileBadge",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "vehicle_id",
  imageField: "document_url",
  searchFields: ["vehicle_id", "receipt_no", "vendor_name"],
});

export const insuranceRecordDoctype = defineDoctype({
  id: "insurance_record",
  moduleId: "fleet",
  table: insuranceRecords,
  label: "ประวัติประกันภัย",
  icon: "ShieldCheck",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "policy_number",
  imageField: "document_url",
  searchFields: ["vehicle_id", "policy_number", "provider_name"],
});

export const taxRecordDoctype = defineDoctype({
  id: "tax_record",
  moduleId: "fleet",
  table: taxRecords,
  label: "ประวัติจ่ายภาษีรถ",
  icon: "Receipt",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "receipt_no",
  imageField: "document_url",
  searchFields: ["vehicle_id", "receipt_no", "vendor_name"],
});

export const vehicleImageDoctype = defineDoctype({
  id: "vehicle_image",
  moduleId: "fleet",
  table: fleetVehicleImages,
  label: "รูปรถ",
  icon: "Image",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("vehicle", "images", "vehicle_id", "sort_order"),
  numberingField: null,
  displayField: "caption",
  imageField: "image_url",
  searchFields: ["caption", "image_type_id"],
});

export const vehicleDocumentDoctype = defineDoctype({
  id: "vehicle_document",
  moduleId: "fleet",
  table: fleetVehicleDocuments,
  label: "เอกสารรถ",
  icon: "FileText",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("vehicle", "documents", "vehicle_id"),
  numberingField: null,
  displayField: "document_name",
  imageField: "file_url",
  searchFields: ["document_name", "document_type", "expiry_date"],
});

/**
 * Every doctype the fleet module wants registered with the platform. Order is
 * preserved in the Admin UI through `listDoctypes()` sort.
 */
export const fleetDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  fleetBrandDoctype,
  fleetModelDoctype,
  fleetSubModelDoctype,
  fleetGenerationDoctype,
  fleetBodyTypeDoctype,
  fleetColorDoctype,
  fleetFuelTypeDoctype,
  fleetTransmissionDoctype,
  fleetYardDoctype,
  fleetVehicleImageTypeDoctype,
  vehicleDoctype,
  vehicleImageDoctype,
  vehicleDocumentDoctype,
  maintenanceTypeDoctype,
  maintenanceScheduleDoctype,
  maintenanceDoctype,
  registrationRecordDoctype,
  insuranceRecordDoctype,
  taxRecordDoctype,
  transferDoctype,
]);
